import { color } from '@mist/design-system'

/**
 * The Birthstone Collection (R-VISUAL-05): twelve months, each carried in Pour
 * Femme and Pour Homme expressions. Poems are concrete and sensory; backdrop
 * tints come from the design-system gem tokens so web, OG, and email agree.
 */
export type CollectionLine = 'femme' | 'homme'

export type MonthKey =
  | 'january' | 'february' | 'march' | 'april' | 'may' | 'june'
  | 'july' | 'august' | 'september' | 'october' | 'november' | 'december'

export type LineRecord = { color: string; poem: string; image: string }

/**
 * The photographed stone shown beside the month title. For ten months it is the
 * bottle's gem; June and November picture the month's alternate birthstones —
 * moonstone and citrine — while the bottle label stays Pearl and Topaz, so the
 * card reads "Pearl / Moonstone — June" and the thumbnail names the stone shown.
 */
export type StoneRecord = { name: string; image: string }

export type BirthstoneEntry = {
  key: MonthKey
  month: string
  gem: string
  stone: StoneRecord
  femme: LineRecord
  homme: LineRecord
}

const gems = color.gems

/**
 * Commerce constants (R-BRAND-02): one birthstone bottle and any special
 * edition, priced in whole US dollars. The API prices every checkout line from
 * these through `priceCartLineCents`; the browser only displays them.
 */
export const COLLECTION_PRICE_USD = 240
export const EDITION_PRICE_USD = 380
export const COMMERCE_CURRENCY = 'USD'

function entry(
  month: string,
  gem: string,
  colors: { femme: string; homme: string },
  poems: { femme: string; homme: string },
  stone?: string,
): BirthstoneEntry {
  const key = month.toLowerCase() as MonthKey
  const slug = gem.toLowerCase()
  const stoneName = stone ?? gem
  return {
    key,
    month,
    gem,
    stone: { name: stoneName, image: `${stoneName.toLowerCase()}-${key}` },
    femme: { color: colors.femme, poem: poems.femme, image: `${slug}-${key}-femme` },
    homme: { color: colors.homme, poem: poems.homme, image: `${slug}-${key}-homme` },
  }
}

/**
 * The card title: "Garnet — January", or "Pearl / Moonstone — June" when the
 * pictured stone differs from the bottle's gem.
 */
export function birthstoneTitle(entry: Pick<BirthstoneEntry, 'gem' | 'stone' | 'month'>): string {
  const lead = entry.stone.name === entry.gem ? entry.gem : `${entry.gem} / ${entry.stone.name}`
  return `${lead} — ${entry.month}`
}

export const BIRTHSTONE_COLLECTION: readonly BirthstoneEntry[] = [
  entry('January', 'Garnet', { femme: gems.garnetFemme, homme: gems.garnetHomme }, {
    femme: 'She burns quietly, like wine held up to winter light',
    homme: 'He is the ember that never asks permission to burn',
  }),
  entry('February', 'Amethyst', { femme: gems.amethystFemme, homme: gems.amethystHomme }, {
    femme: 'Her mystery is a room no one has ever fully entered',
    homme: "His silence holds more than most men's words ever will",
  }),
  entry('March', 'Aquamarine', { femme: gems.aquamarineFemme, homme: gems.aquamarineHomme }, {
    femme: 'She carries the sea in her eyes and salt on her breath',
    homme: 'He moves like deep water — calm on the surface, powerful beneath',
  }),
  entry('April', 'Diamond', { femme: gems.diamondFemme, homme: gems.diamondHomme }, {
    femme: 'Cold and brilliant, she cuts through everything without trying',
    homme: 'He was forged under pressure and came out unbreakable',
  }),
  entry('May', 'Emerald', { femme: gems.emeraldFemme, homme: gems.emeraldHomme }, {
    femme: 'She is the forest after rain — deep, alive, and ancient',
    homme: 'He is rooted so deep the storm never frightens him',
  }),
  entry('June', 'Pearl', { femme: gems.pearlFemme, homme: gems.pearlHomme }, {
    femme: 'Born from something wounded, she became the most beautiful thing',
    homme: 'He is rare — smooth where others are rough, strong where others break',
  }, 'Moonstone'),
  entry('July', 'Ruby', { femme: gems.rubyFemme, homme: gems.rubyHomme }, {
    femme: 'Her love is not soft — it is red and it is absolute',
    homme: 'His passion is not a feeling, it is a force',
  }),
  entry('August', 'Peridot', { femme: gems.peridotGemFemme, homme: gems.peridotGemHomme }, {
    femme: 'She grew in the heat of the earth and still chose to be light',
    homme: 'He carries gold in his bones and fire in his quiet',
  }),
  entry('September', 'Sapphire', { femme: gems.sapphireFemme, homme: gems.sapphireHomme }, {
    femme: 'There are depths in her that even silence cannot reach',
    homme: 'He is the kind of deep that takes your breath before you reach the bottom',
  }),
  entry('October', 'Opal', { femme: gems.opalFemme, homme: gems.opalHomme }, {
    femme: 'She is never the same twice, and that is her greatest truth',
    homme: 'He contains multitudes — none of them simple, all of them real',
  }),
  entry('November', 'Topaz', { femme: gems.topazFemme, homme: gems.topazHomme }, {
    femme: 'She is autumn gold — warm, inevitable, and breathtaking',
    homme: 'He is the last warmth of the year — rich, amber, and unforgettable',
  }, 'Citrine'),
  entry('December', 'Turquoise', { femme: gems.turquoiseFemme, homme: gems.turquoiseHomme }, {
    femme: 'She is the horizon where the sea forgets the sky',
    homme: 'He is ancient and open — vast as the desert sky above him',
  }),
] as const

/**
 * Special-occasion presentations shown on the landing editions grid, in the
 * family draft's order. Each is a configured set priced at EDITION_PRICE_USD;
 * `cta` is the draft's action label for its card.
 */
export type SpecialEdition = {
  id: string
  name: string
  image: string
  body: string
  cta: string
}

export const SPECIAL_EDITIONS: readonly SpecialEdition[] = [
  {
    id: 'wedding',
    name: 'The Wedding Edition',
    image: 'edition-wedding',
    body: 'Features two birthstones in a pearl white collector box with gold-plated plaques and keepsake story cards.',
    cta: 'Configure Set',
  },
  {
    id: 'anniversary',
    name: 'The Anniversary Edition',
    image: 'edition-anniversary',
    body: 'Celebrates your milestone with dual birthstones housed in an ocean-blue leather box with gold anniversary emblems.',
    cta: 'Configure Set',
  },
  {
    id: 'couple',
    name: 'The Couple Edition',
    image: 'edition-couple',
    body: 'Pair two birthstones inside a dark collector box featuring twin heart detailing and dual story cards.',
    cta: 'Configure Set',
  },
  {
    id: 'garnet-duo',
    name: 'Garnet Femme & Homme Duo',
    image: 'edition-garnet-duo',
    body: 'Matching January collector set featuring both Femme and Homme expressions in a double presentation box.',
    cta: 'Select Duo',
  },
  {
    id: 'collector',
    name: 'The Collector Edition',
    image: 'edition-collector',
    body: 'Presented in a magnetic rigid box with a gold pull tab, certificate of authenticity, and inner lid poem.',
    cta: 'View Details',
  },
  {
    id: 'unboxing',
    name: 'The Unboxing Experience',
    image: 'edition-unboxing',
    body: 'Every bottle features a hidden poem inside the box panels and custom side-panel signatures.',
    cta: 'Explore Experience',
  },
] as const

/** Atelier field and action copy (the strings the collection card renders). */
export const ATELIER_COPY = {
  engravingLabel: 'Custom bottle engraving',
  engravingPlaceholder: 'Enter name or date…',
  signatureLabel: 'Box signature (full or short)',
  signatureDefault: 'Boorenie',
  signatureHint: 'Set into the box side panel.',
  addToCart: `Add to Cart — $${COLLECTION_PRICE_USD}`,
  editionPriceNote: `$${EDITION_PRICE_USD} — box signature included`,
} as const

/** Every user-facing collection/editions string, for banned-copy guard coverage. */
export const COLLECTION_COPY: readonly string[] = [
  ...BIRTHSTONE_COLLECTION.flatMap((e) => [birthstoneTitle(e), e.stone.name, e.femme.poem, e.homme.poem]),
  ...SPECIAL_EDITIONS.flatMap((e) => [e.name, e.body, `${e.cta} — $${EDITION_PRICE_USD}`]),
  ...Object.values(ATELIER_COPY),
  'Pour Femme',
  'Pour Homme',
]
